import { useRouter } from "next/router";
import { useEffect } from "react";

import useShop from "../hooks/useShop.hook";
import { SalesLocation, ShopByCode, ShopFull, ShopTiny } from "../types/shop";
import { get, post } from "./fetch.core";
import Cookies from "js-cookie";
import { getDomain } from "./cookies.core";

export async function restrictShopAccess(data: any) {
  const res = await post("/admin/shop/restrict", data);

  return res.data;
}

export async function getRestrictionInfo() {
  const res = await get<any>("/admin/shop/getRestiction");
  return res.data;
}

export async function roleRestriction(shopId: string) {
  const res = await get<any>(`/admin/shop/roleRestriction/${shopId}`);

  return res.data;
}

export async function getShopList() {
  if (Cookies.get("loginTokenAdmin") === undefined) {
    return undefined;
  }

  const res = await get<ShopTiny[]>("/admin/shop/list", { useToken: true, useShopId: false });
  return res.data;
}

export async function getShop() {
  const res = await get<ShopFull>("/admin/shop/get");
  return res.data;
}

export async function getShopByCode(code) {
  const res = await get<ShopByCode>(`/public/shop/getByCode/${code}`);
  return res.data;
}

export function useAuthorizedPosRoute() {
  const router = useRouter();
  const { selectedShop } = useShop();

  useEffect(() => {
    if (selectedShop && !selectedShop.posEnabled) {
      router.push("/");
    }
  }, [selectedShop, router]);
}

export async function update(data: any) {
  sanitizeUpdateData(data);
  await post("/admin/shop/update", data);
}

export async function create(data: any) {
  await post("/admin/shop/create", data);
}

export async function addSalesLocation(data: SalesLocation) {
  return await post("/admin/sales-location/create", data);
}

export async function getSalesLocation(id: string) {
  return await get<SalesLocation>(`/admin/sales-location?id=${id}`);
}
interface SalesLocationList {
  size: number;
  list: SalesLocation[];
}

export async function listSalesLocation(data: any) {
  return await post<SalesLocationList>("/admin/sales-location/list", data);
}

export async function updateSalesLocation(data: SalesLocation) {
  await post("/admin/sales-location/update", data);
}

export async function deleteSalesLocation(id) {
  await post("/admin/sales-location/delete", { id });
}

export interface WarrantyClaim {
  productId: any;

  purchaseDate: string; // yyyy-MM-dd
  serialNumber: string;
  receiptMediaUrl?: string;

  name: string;
  tel?: string;
  email?: string;
  completed: Boolean;

  createdAt?: Date;
}

interface WarrantyClaimList {
  size: number;
  list: WarrantyClaim[];
}

export async function listWarrantyClaim(data: any) {
  return await post<WarrantyClaimList>("/admin/warranty/subscribe/list", data);
}

export async function togglePos(data: { posEnabled: boolean }) {
  await post("/admin/shop/togglePos", data);
}

export async function updateStripeInfo(data: any) {
  await post("/admin/shop/updateStripeInfo", data);
}

export async function updatePaypalInfo(data: any) {
  await post("/admin/shop/updatePaypalInfo", data);
}

export async function updateKpayInfo(data: any) {
  await post("/admin/shop/updateKpayinfo", data);
}

export async function updatePaymeInfo(data: any) {
  await post("/admin/shop/updatePaymeinfo", data);
}

export async function getStripeInfo() {
  const res = await get<any>("/admin/shop/getStripeInfo");
  return res.data;
}

export async function getPaypalInfo() {
  const res = await get<any>("/admin/shop/getPaypalInfo");
  return res.data;
}

export async function getKpayInfo() {
  const res = await get<any>("/admin/shop/getKpayInfo");
  return res.data;
}
export async function getPaymeInfo() {
  const res = await get<any>("/admin/shop/getPaymeInfo");
  return res.data;
}

function sanitizeUpdateData(data: any) {
  data.eshopOrderCancelTime = Number(data.eshopOrderCancelTime) * 60;
  data.defaultRetailOrderCancelTime = Number(data.defaultRetailOrderCancelTime) * 60;
  data.retailOrderCancelTimes = data.retailOrderCancelTimes
    .filter((r) => r.time && r.retailShopId)
    .map((d) => ({ ...d, time: Number(d.time) * 60 }));

  if (data.orderCreatedEmailDelayMinutes !== undefined) {
    data.orderCreatedEmailDelayMinutes = Number(data.orderCreatedEmailDelayMinutes) || 0;
  }

  if (data.rewardExpireAfter === "") delete data.rewardExpireAfter;
}

export function setShopId(shopId: string) {
  Cookies.set("shopId", shopId, {
    domain: getDomain(),
    expires: 365,
    sameSite: "strict",
  });

  sessionStorage.setItem("shopId", shopId);
}

export function setShopIdToCookies(shopId: string) {
  Cookies.set("shopId", shopId, {
    domain: getDomain(),
    expires: 365,
    sameSite: "strict",
  });
}

export function setShopIdToSession(shopId: string) {
  sessionStorage.setItem("shopId", shopId);
}

export function getShopId() {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem("shopId") ?? Cookies.get("shopId");
  }

  return Cookies.get("shopId");
}

const shopCore = {
  getShopList,
  getShop,
  update,
  create,
  togglePos,
  updateStripeInfo,
  updatePaypalInfo,
  updateKpayInfo,
  updatePaymeInfo,
  getStripeInfo,
  getPaypalInfo,
  getKpayInfo,
  getPaymeInfo,
  setShopId,
  getShopId,
  addSalesLocation,
  getSalesLocation,
  listSalesLocation,
  updateSalesLocation,
  deleteSalesLocation,
  listWarrantyClaim,
  // setSelectedShopFullToStore,
};

export default shopCore;
